// VIB-724b: UI for å mass-overstyre task_status fra Monday-fase-status.
//
// Flyt:
//  1. Admin velger ett eller flere prosjekter via ImportProjectSelector
//  2. Klikker "Hent Monday-status" → edge function returnerer fase-status per
//     prosjekt + vi henter assignments fra Supabase
//  3. Preview-tabell: én rad per assignment, viser nåværende → ny status
//  4. Rader hvor forward-only-policy ikke tillater oppdatering er disabled
//  5. Admin huker av rader (default: alle endrings-rader), klikker "Synk valgt"
//
// Forward-only er enforced både her (i preview) og i hooken — defense in depth.

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { ImportProjectSelector } from "@/components/admin/ImportProjectSelector";
import { supabase } from "@/lib/supabase";
import {
  mapMondayStatus,
  shouldUpdate,
  useApplyMondayPhaseStatus,
  useMondayPhaseSnapshot,
  type MondayStatus,
  type PhaseKey,
  type SnapshotResponse,
} from "@/hooks/use-monday-phase-sync";
import { cn } from "@/lib/utils";

/** UI-label per phase-key. Match `PHASE_LABELS` i offer-types-konvensjonen. */
const PHASE_LABEL: Record<PhaseKey, string> = {
  preprod: "Preproduksjon",
  production: "Produksjon",
  postprod: "Postproduksjon",
};

/** UI-label per task_status. */
const STATUS_LABEL: Record<string, string> = {
  not_started: "Ikke startet",
  in_progress: "Pågår",
  completed: "Ferdig (venter godkjenning)",
  approved: "Godkjent",
  rejected: "Avvist",
  unassigned: "Ikke tildelt",
};

interface AssignmentRow {
  assignmentId: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  phase: PhaseKey;
  description: string;
  userName: string;
  currentStatus: string;
}

interface PreviewRow extends AssignmentRow {
  mondayLabel: string | null;
  newStatus: MondayStatus;
  canUpdate: boolean;
}

interface RawAssignment {
  id: string;
  task_status: string;
  budget_lines: {
    description: string | null;
    phase: string | null;
    project_id: string;
    projects: {
      project_code: string | null;
      name: string | null;
    } | null;
  } | null;
  users: {
    full_name: string | null;
  } | null;
}

/**
 * Hent assignments for valgte project_codes. Brukes lokalt i komponenten —
 * ikke gjenbrukbart nok til å være en delt hook.
 */
function useAssignmentsForProjectCodes(projectCodes: string[], enabled: boolean) {
  const sortedCodes = [...projectCodes].sort();
  return useQuery<AssignmentRow[]>({
    queryKey: ["phase-sync-assignments", sortedCodes.join(",")],
    enabled: enabled && projectCodes.length > 0,
    queryFn: async () => {
      // Slå opp project_ids fra project_codes først (assignments joiner
      // gjennom budget_lines.project_id).
      const { data: projects, error: projErr } = await supabase
        .from("projects")
        .select("id, project_code, name")
        .in("project_code", projectCodes)
        .is("deleted_at", null);
      if (projErr) throw projErr;
      const projectIds = (projects ?? []).map((p) => p.id);
      if (projectIds.length === 0) return [];

      const projectMeta = new Map<string, { code: string; name: string }>();
      for (const p of projects ?? []) {
        projectMeta.set(p.id, {
          code: (p.project_code as string) ?? "",
          name: (p.name as string) ?? "(uten navn)",
        });
      }

      const { data: assignments, error: aErr } = await supabase
        .from("budget_line_assignments")
        .select(
          `
          id,
          task_status,
          budget_lines!inner(
            description,
            phase,
            project_id,
            projects(project_code, name)
          ),
          users!budget_line_assignments_user_id_fkey(full_name)
        `,
        )
        .in("budget_lines.project_id", projectIds);
      if (aErr) throw aErr;

      const rows: AssignmentRow[] = [];
      for (const a of (assignments ?? []) as unknown as RawAssignment[]) {
        const bl = a.budget_lines;
        if (!bl) continue;
        const phase = bl.phase as PhaseKey;
        // Kun phase-keys vi støtter (preprod/production/postprod). Andre
        // (reise, pl osv.) har ikke fase-status-kolonne i Monday.
        if (phase !== "preprod" && phase !== "production" && phase !== "postprod") {
          continue;
        }
        const meta = projectMeta.get(bl.project_id);
        rows.push({
          assignmentId: a.id,
          projectId: bl.project_id,
          projectCode: meta?.code ?? "",
          projectName: meta?.name ?? "(uten navn)",
          phase,
          description: bl.description ?? "(uten beskrivelse)",
          userName: a.users?.full_name ?? "Ukjent",
          currentStatus: a.task_status ?? "not_started",
        });
      }
      return rows;
    },
    staleTime: 30 * 1000,
  });
}

function buildPreviewRows(
  assignments: AssignmentRow[],
  snapshot: SnapshotResponse,
): PreviewRow[] {
  return assignments.map((row) => {
    const projectSnapshot = snapshot[row.projectCode];
    const mondayLabel = projectSnapshot ? projectSnapshot[row.phase] : null;
    const newStatus = mapMondayStatus(mondayLabel);
    const canUpdate = shouldUpdate(row.currentStatus, newStatus);
    return { ...row, mondayLabel, newStatus, canUpdate };
  });
}

export function MondayPhaseStatusSync() {
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [snapshotRequested, setSnapshotRequested] = useState(false);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<Set<string>>(
    new Set(),
  );

  const snapshotQuery = useMondayPhaseSnapshot(selectedCodes, snapshotRequested);
  const assignmentsQuery = useAssignmentsForProjectCodes(
    selectedCodes,
    snapshotRequested,
  );
  const applyMut = useApplyMondayPhaseStatus();

  const previewRows = useMemo<PreviewRow[]>(() => {
    if (!snapshotQuery.data || !assignmentsQuery.data) return [];
    return buildPreviewRows(assignmentsQuery.data, snapshotQuery.data);
  }, [snapshotQuery.data, assignmentsQuery.data]);

  // Auto-select alle rader som faktisk kan oppdateres når preview lastes.
  // Vi bruker en effekt-erstatning: når previewRows endres og selectedAssignmentIds
  // er tom, fyller vi med alle canUpdate-rader. Admin kan så huke av/på.
  const updatableIds = useMemo(
    () => previewRows.filter((r) => r.canUpdate).map((r) => r.assignmentId),
    [previewRows],
  );

  // Hvis preview endres (ny snapshot, andre prosjekter), reset selection til
  // alle oppdaterbare. Bruker stringified IDs som dependency for å unngå
  // re-render-loops.
  const updatableKey = updatableIds.join(",");
  useEffect(() => {
    setSelectedAssignmentIds(new Set(updatableIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatableKey]);

  const handleFetch = () => {
    setSnapshotRequested(true);
  };

  const handleReset = () => {
    setSnapshotRequested(false);
    setSelectedAssignmentIds(new Set());
  };

  const toggleAssignment = (id: string) => {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSync = async () => {
    const updates = previewRows
      .filter((r) => r.canUpdate && selectedAssignmentIds.has(r.assignmentId))
      .map((r) => ({ assignmentId: r.assignmentId, newStatus: r.newStatus }));
    if (updates.length === 0) return;
    await applyMut.mutateAsync(updates);
    handleReset();
  };

  const isLoading = snapshotQuery.isLoading || assignmentsQuery.isLoading;
  const hasError = snapshotQuery.error || assignmentsQuery.error;
  const hasData = snapshotRequested && previewRows.length > 0;
  const updatableCount = previewRows.filter((r) => r.canUpdate).length;
  const selectedCount = previewRows.filter(
    (r) => r.canUpdate && selectedAssignmentIds.has(r.assignmentId),
  ).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Synk fase-status fra Monday
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Henter fase-status (Pre / Prod / Post) per Monday-prosjekt og
          oppgraderer task_status på alle oppgaver i den fasen.{" "}
          <span className="font-medium">Forward-only</span> — eksisterende
          godkjente oppgaver nedgraderes aldri.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Prosjekter ({selectedCodes.length})
          </label>
          <ImportProjectSelector
            selectedCodes={selectedCodes}
            onChange={(codes) => {
              setSelectedCodes(codes);
              handleReset();
            }}
          />
        </div>

        <div className="flex items-center gap-2 pt-2 border-t">
          <Button
            variant="outline"
            onClick={handleFetch}
            disabled={selectedCodes.length === 0 || isLoading}
          >
            {isLoading ? (
              <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <SrIcon name="download" className="w-4 h-4 mr-1.5" />
            )}
            Hent Monday-status
          </Button>
          {hasData && (
            <Button
              onClick={handleSync}
              disabled={selectedCount === 0 || applyMut.isPending}
            >
              {applyMut.isPending ? (
                <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <SrIcon name="check" className="w-4 h-4 mr-1.5" />
              )}
              Synk valgt ({selectedCount})
            </Button>
          )}
          {hasData && (
            <span className="ml-auto text-xs text-muted-foreground tabular-nums">
              {previewRows.length} oppgaver — {updatableCount} kan oppgraderes
            </span>
          )}
        </div>

        {hasError && (
          <div className="text-xs text-destructive border border-destructive/40 rounded p-2">
            {(snapshotQuery.error ?? assignmentsQuery.error) instanceof Error
              ? (snapshotQuery.error ?? assignmentsQuery.error)?.message
              : "Ukjent feil"}
          </div>
        )}

        {hasData && (
          <div className="border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-2 py-1.5 w-8"></th>
                  <th className="px-2 py-1.5">Prosjekt</th>
                  <th className="px-2 py-1.5">Fase</th>
                  <th className="px-2 py-1.5">Oppgave</th>
                  <th className="px-2 py-1.5">Person</th>
                  <th className="px-2 py-1.5">Nå</th>
                  <th className="px-2 py-1.5">→ Ny</th>
                  <th className="px-2 py-1.5">Monday</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => {
                  const checked = selectedAssignmentIds.has(row.assignmentId);
                  return (
                    <tr
                      key={row.assignmentId}
                      className={cn(
                        "border-t",
                        !row.canUpdate && "opacity-50",
                      )}
                    >
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!row.canUpdate}
                          onChange={() => toggleAssignment(row.assignmentId)}
                          aria-label={`Oppdater ${row.description}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="tabular-nums font-medium">
                          {row.projectCode}
                        </span>{" "}
                        <span className="text-muted-foreground truncate max-w-[200px] inline-block align-bottom">
                          {row.projectName}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">{PHASE_LABEL[row.phase]}</td>
                      <td className="px-2 py-1.5">{row.description}</td>
                      <td className="px-2 py-1.5">{row.userName}</td>
                      <td className="px-2 py-1.5">
                        {STATUS_LABEL[row.currentStatus] ?? row.currentStatus}
                      </td>
                      <td className="px-2 py-1.5">
                        {row.canUpdate ? (
                          <span className="font-medium">
                            {STATUS_LABEL[row.newStatus] ?? row.newStatus}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">
                            (ingen endring)
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">
                        {row.mondayLabel ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {snapshotRequested && !isLoading && previewRows.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Ingen oppgaver funnet for valgte prosjekter. Sjekk at prosjektene
            har Monday-kobling og at oppgavene har en gyldig fase.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
